import { Anchor, Button, H5 } from "@sellar-io/components";
import { Icon, ListElement, SpinnerLegacy } from "../../../../../components";
import { twMerge } from "tailwind-merge";
import { formatCurrency } from "@sellar-io/common";
import { useMemo, useRef, useState } from "react";
import { useSellarImportPayout } from "../hooks/useSellarImportPayout";
import { SellarImportPayoutDocumentSummary } from "./SellarImportPayoutDocumentSummary";
import { AdminOnly } from "../../../../../components/authFilter/AdminOnly";
import { ORDER_DETAILS } from "../../../../../constants/routes";
import { formatDate } from "../../../../../utils/formatDate";
import { UpdateSellarPayoutDialog } from "./UpdateSellarPayoutDialog";

const Dot = ({ color }: { color: string }) => (
  <span className={`inline-block h-2 w-2 rounded-full ${color}`}></span>
);

const PayoutCard = ({
  item,
}: {
  item: {
    type: "invoice" | "creditNote";
    total: number;
    customerName: string;
    OrderId: number | null;
    createdAt: Date;
    paidAt: Date | null;
  };
}) => {
  const link = item.OrderId
    ? ORDER_DETAILS.href({ orderId: item.OrderId })
    : ".";
  return (
    <ListElement>
      <div className={"flex flex-col gap-1"}>
        <span className="text-xs">
          {item.type === "invoice" ? (
            <Anchor href={link} target="_blank">
              Order #{item.OrderId || ""}
            </Anchor>
          ) : (
            ``
          )}
        </span>

        <span className="text-sm">
          {item.customerName} <br />
          Price (ex. VAT): {item.type === "invoice" ? "" : "-"}
          {formatCurrency(item.total)} <br />
          Created:{" "}
          {formatDate(item.createdAt, {
            format: "DD/MM/YY",
          })}
          <br />
          Paid:{" "}
          {item.paidAt
            ? formatDate(item.paidAt, {
                format: "DD/MM/YY",
              })
            : "Not received"}
        </span>
      </div>
    </ListElement>
  );
};

export const PayoutDetailsCardList = ({
  selectedPayoutId,
  onHide,
}: {
  selectedPayoutId: number;
  onHide: () => void;
}) => {
  const [updatePayoutDialogActive, setUpdatePayoutDialogActive] =
    useState(false);

  const sellarImportPayoutQuery = useSellarImportPayout({
    id: selectedPayoutId,
  });

  const sellarImportPayout = sellarImportPayoutQuery.data;

  const data = useMemo(() => {
    if (!sellarImportPayout) return [];

    const invoiceRows = sellarImportPayout.Orders.map((order) => {
      return {
        orderId: order.id,
        total: order.priceExVat,
        customerBusinessName: order.Retailer.Business.name,
        type: "invoice" as const,
        paidAt: order.paidAt,
        createdAt: order.createdAt,
      };
    });
    const creditNoteRows = sellarImportPayout.CreditNotes.map((creditNote) => {
      return {
        orderId: null,
        total: creditNote.totalExVat,
        customerBusinessName: creditNote.SenderBusiness.name,
        type: "creditNote" as const,
        paidAt: creditNote.paidAt,
        createdAt: creditNote.createdAt,
      };
    });

    return [...invoiceRows, ...creditNoteRows];
  }, [sellarImportPayout]);

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  if (!sellarImportPayout) {
    return <SpinnerLegacy />;
  }

  return (
    <>
      <div
        className={twMerge(
          "[scrollbar-width:none] overflow-auto border-zinc-300 [::-webkit-scrollbar]:hidden",
        )}
        ref={scrollContainerRef}
      >
        <div
          className={twMerge(
            "flex w-full border-collapse flex-wrap items-center justify-between py-4",
          )}
        >
          <div className="flex w-full items-center justify-between gap-2 p-4">
            <H5 className="flex w-full flex-wrap items-center space-x-1 text-sm font-medium">
              <div
                className="cursor-pointer hover:underline"
                onClick={() => onHide()}
              >
                Sellar Import
              </div>
              <Icon icon="CHEVRON_RIGHT" className="m-0" />

              <div>
                Payout #{sellarImportPayout.id}{" "}
                <span className="font-light">
                  (
                  {formatDate(sellarImportPayout.captureStart, {
                    format: "DD/MM/YY",
                  })}{" "}
                  -{" "}
                  {formatDate(sellarImportPayout.captureEnd, {
                    format: "DD/MM/YY",
                  })}
                  )
                </span>
              </div>
            </H5>

            <AdminOnly>
              <Button
                type="button"
                onClick={() => setUpdatePayoutDialogActive(true)}
              >
                <Icon icon="PENCIL" />
                Edit
              </Button>
            </AdminOnly>
          </div>

          <div className="ms-4 inline-flex self-start rounded-lg border border-zinc-300 bg-white py-4">
            <div className="flex flex-col gap-2 border-r px-6">
              <span className="text-xs font-semibold">
                Total order amount (ex. VAT)
              </span>
              <span className="text-xl">
                {formatCurrency(sellarImportPayout.totalRevenueExVat)}
              </span>
            </div>
            <div className="flex flex-col gap-2 px-6">
              <span className="text-xs font-semibold">
                Net payout (ex. VAT)
              </span>
              <span className="flex items-center gap-1 text-xl text-zinc-800">
                <Dot color="bg-emerald-800" />
                {formatCurrency(
                  sellarImportPayout.totalRevenueExVat -
                    sellarImportPayout.totalFeeExVat,
                )}
              </span>
            </div>
          </div>
        </div>

        <div className="mt-4">
          <SellarImportPayoutDocumentSummary
            sellarImportPayoutId={selectedPayoutId}
            revenueInvoice={sellarImportPayout.RevenueInvoice}
            feeInvoice={sellarImportPayout.FeeInvoice}
          />
        </div>

        {data.map((item) => (
          <PayoutCard
            item={{
              type: item.type,
              OrderId: item.orderId,
              total: item.total,
              customerName: item.customerBusinessName,
              paidAt: item.paidAt,
              createdAt: item.createdAt,
            }}
            key={`${item.customerBusinessName}-${item.type}-${item.total}`}
          />
        ))}
      </div>

      <UpdateSellarPayoutDialog
        active={updatePayoutDialogActive}
        onDismiss={() => setUpdatePayoutDialogActive(false)}
        existingPayout={sellarImportPayout}
      />
    </>
  );
};
